import type {
  DeployStatus,
  DeployStatusState,
  DeployStatusStage,
} from '@volley/crucible-ipc-types/ipc-channels';

/**
 * Issue #637 — SINGLE SOURCE OF TRUTH for "does `<DeployStatusCard>`
 * render anything for this snapshot?". The card returns `null` for the
 * quiet `idle` state (no active deploy, or the quiet `unavailable`
 * classification: no PAT / no remote / no runs / a non-success
 * conclusion). Mount points (the Workspace section, the Dashboard pill)
 * MUST gate their padded+bordered wrapper on this same predicate so the
 * wrapper and the card agree — otherwise the wrapper mounts on a truthy
 * `deployStatus` while the card renders nothing, leaving an empty
 * bordered band (Bugbot round 5; the #E fix made `idle` more common).
 *
 * Accepts `undefined` so callers can pass the hook's value directly:
 * `deployStatusHasContent(deployStatus)`. Typed as a type guard so a
 * truthy result NARROWS the value to `DeployStatus` for the JSX that
 * follows (the wrapper can then pass `status={deployStatus}` without a
 * non-null assertion).
 */
export function deployStatusHasContent(status: DeployStatus | undefined): status is DeployStatus {
  return status !== undefined && status.state !== 'idle';
}

/**
 * Issue #637 — `<DeployStatusCard>` is the renderer half of the
 * ambient deploy-status surface. It renders the latest `DeployStatus`
 * snapshot the main-side watcher pushes on `deploy:status`: the coarse
 * pipeline state, a per-stage strip (build → deploy → TV), a deep-link
 * to the GitHub Actions run, and — the whole point of the issue — a
 * clear "you can test it on the TV now" signal when the bundle goes
 * live.
 *
 * It deliberately reuses the visual idiom of `<DeployFailureCard>`
 * (#277): rounded bordered card, bg-tinted by tone, monospace run link,
 * small uppercase header. The pipeline-state vocabulary is the FINE
 * `DeployStatusState` (build/deploy/tv legs) rather than the deploy-
 * BUTTON's coarse `DeployState` — different concern (pipeline progress
 * vs the Deploy button's action-state).
 *
 * Quiet states: an `idle` snapshot (no active deploy, or a quiet
 * `unavailable` — no PAT / no remote / no runs) renders NOTHING. The
 * card only appears once there is a pipeline to report, so a workspace
 * with no recent deploy stays uncluttered. A long `tv-warming` is
 * expected on a brand-new game (DNS warm-up, bifrost#110) and is framed
 * as "almost there", NOT a failure.
 */

export interface DeployStatusCardProps {
  status: DeployStatus;
  /** Open the GitHub Actions run for diagnostics. Defaults to
   *  `window.open` — injectable so SSR / unit tests don't need a real
   *  window. */
  onOpenRun?: (url: string) => void;
}

interface StatePresentation {
  /** Uppercase header label. */
  label: string;
  /** One-line body copy under the header. */
  copy: string;
  /** Tailwind border + bg tint for the card. */
  cardClassName: string;
  /** Tailwind classes for the header text. */
  headerClassName: string;
}

const PRESENTATION: Record<DeployStatusState, StatePresentation> = {
  idle: {
    label: 'Deploy',
    copy: '',
    cardClassName: 'border-white/10 bg-white/5',
    headerClassName: 'text-white/50',
  },
  building: {
    label: 'Building',
    copy: 'CI is building and checking the new bundle.',
    cardClassName: 'border-blue-500/30 bg-blue-500/5',
    headerClassName: 'text-blue-300/80',
  },
  pushing: {
    label: 'Pushing',
    copy: 'Pushing your changes to GitHub.',
    cardClassName: 'border-blue-500/30 bg-blue-500/5',
    headerClassName: 'text-blue-300/80',
  },
  deploying: {
    label: 'Deploying',
    copy: 'Build passed — rolling the new bundle out to the TV cluster.',
    cardClassName: 'border-yellow-500/30 bg-yellow-500/5',
    headerClassName: 'text-yellow-300/80',
  },
  'tv-warming': {
    label: 'TV warming',
    copy: 'Deployed — waiting for the TV to serve the new build. New games can take a few minutes (DNS warm-up).',
    cardClassName: 'border-yellow-500/30 bg-yellow-500/5',
    headerClassName: 'text-yellow-300/80',
  },
  'tv-ready': {
    label: 'Live on the TV',
    copy: 'The new build is live — you can test it on the TV now.',
    cardClassName: 'border-green-500/30 bg-green-500/5',
    headerClassName: 'text-green-300/80',
  },
  failed: {
    label: 'Deploy failed',
    copy: 'The deploy workflow failed. Open the run on GitHub for details.',
    cardClassName: 'border-red-500/30 bg-red-500/5',
    headerClassName: 'text-red-300/80',
  },
};

/** Human label for each pipeline stage key. */
const STAGE_LABELS: Record<string, string> = {
  build: 'Build',
  deploy: 'Deploy',
  tv: 'TV ready',
};

/** Glyph + colour per stage status. */
function stageGlyph(status: DeployStatusStage['status']): { glyph: string; className: string } {
  switch (status) {
    case 'done':
      return { glyph: '✓', className: 'text-green-400' };
    case 'running':
      return { glyph: '●', className: 'text-yellow-400 animate-pulse' };
    case 'failed':
      return { glyph: '✕', className: 'text-red-400' };
    case 'pending':
    default:
      return { glyph: '○', className: 'text-white/30' };
  }
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export function DeployStatusCard({
  status,
  onOpenRun,
}: DeployStatusCardProps): React.JSX.Element | null {
  // Quiet states render nothing — the card only surfaces an ACTIVE
  // pipeline. Uses the shared `deployStatusHasContent` predicate so the
  // card's null-return and the wrappers that gate on it can never
  // diverge (Bugbot round 5 — empty bordered band).
  if (!deployStatusHasContent(status)) return null;

  const pres = PRESENTATION[status.state];
  const handleOpenRun = (url: string): void => {
    if (onOpenRun) {
      onOpenRun(url);
      return;
    }
    window.open(url, '_blank', 'noreferrer');
  };

  return (
    <div
      data-testid="deploy-status-card"
      data-deploy-state={status.state}
      className={`mb-3 rounded-lg border p-4 ${pres.cardClassName}`}
      role="status"
    >
      <div className="mb-2 flex items-center justify-between">
        <span
          data-testid="deploy-status-card-header"
          className={`text-xs font-medium uppercase tracking-wider ${pres.headerClassName}`}
        >
          {pres.label}
        </span>
        {status.workflowRunUrl && (
          <button
            type="button"
            data-testid="deploy-status-view-run"
            onClick={() => handleOpenRun(status.workflowRunUrl as string)}
            className="text-[11px] text-white/40 underline-offset-2 transition-colors hover:text-white/70 hover:underline"
          >
            View run
          </button>
        )}
      </div>

      {pres.copy && (
        <p data-testid="deploy-status-copy" className="mb-3 text-xs text-white/60">
          {pres.copy}
        </p>
      )}

      <ul data-testid="deploy-status-stages" className="space-y-1">
        {status.stages.map((stage) => {
          const g = stageGlyph(stage.status);
          return (
            <li
              key={stage.name}
              data-testid={`deploy-status-stage-${stage.name}`}
              data-stage-status={stage.status}
              className="flex items-center gap-2 text-xs"
            >
              <span className={`w-3 text-center font-mono ${g.className}`} aria-hidden="true">
                {g.glyph}
              </span>
              <span className="text-white/70">{STAGE_LABELS[stage.name] ?? stage.name}</span>
              {typeof stage.durationMs === 'number' && stage.durationMs > 0 && (
                <span className="ml-auto font-mono text-[10px] text-white/35">
                  {formatDuration(stage.durationMs)}
                </span>
              )}
            </li>
          );
        })}
      </ul>

      {status.state === 'tv-ready' && status.deployedAt && (
        <div data-testid="deploy-status-deployed-at" className="mt-3 text-[11px] text-white/45">
          Live at{' '}
          {new Date(status.deployedAt).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
          {status.deployedSha && (
            <span className="ml-2 font-mono text-white/35">{status.deployedSha.slice(0, 7)}</span>
          )}
        </div>
      )}

      {status.error && status.error.code !== 'unavailable' && (
        <div data-testid="deploy-status-error" className="mt-3 text-[11px] text-white/50">
          {status.error.message}
        </div>
      )}
    </div>
  );
}
